import type * as React from "react";
import { type FC, type ReactNode, useContext, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { Box, Button, Link, Stack, TextField } from "~/common/mui";
import { ActionIconButton } from "~/common/components/Button/ActionIconButton";
import Checkbox from "~/common/components/Checkbox";
import ProductImage from "~/domains/product/components/ProductImage";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import { GET_LINEAR_CONFIGURATOR_URL_FOR_ITEM } from "~/features/configurator/Linear/constants";
import { type ProductData } from "~/domains/product/types/ProductData";
import { DefaultConfirmationContext } from "~/features/configurator/Linear/components/ConfirmationContext";
import {
  SavedConfigContext,
  type SavedConfigurationItem,
} from "~/features/savedConfigurations/savedConfigurationsContext";
import { CommentButton } from "~/common/components/CommentButton/CommentButton";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { Card } from "~/features/configurator/Linear/components/Card";
import { canSeeSalesFinder } from "~/features/navigation/components/headerMenu/navigationVisibility";
import { CART_PRODUCT_IMAGE_WIDTHS } from "~/domains/cart/utils/cartConstants";
import { ImageFormat, ImageType } from "~/domains/product/types/ProductDataDTO";
import { resolveUrl } from "~/utils/url/urlResolver";
import { redirectToUrl } from "~/utils/url/urlUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import ShareIcon from "@mui/icons-material/Share";
import ShareConfigurationsMenu from "~/features/savedConfigurations/components/ShareConfigurationsMenu";
import ShareConfigurationsViaEmailPopup from "~/features/savedConfigurations/components/ShareConfigurationsViaEmailPopup";
import { useTranslation } from "react-i18next";

import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { useForcedConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { ConfiguratorType, getConfiguratorTypeFromProductCode } from "~/domains/configurator/types/ConfiguratorType";
import { isTimelineConfiguratorProductCode, UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { loadConfiguratorV2 } from "~/features/configurator-v2/common/data/configuratorV2Api";
import { adaptConfiguratorConfiguration } from "~/features/configurator-v2/common/data/adaptConfiguratorConfiguration";
import {
  adaptConfiguratorRuntimeContext,
  configuratorConfigurationKey,
  createConfiguratorInitialExpansion,
  markNextConfiguratorInitialExpansion,
  type ConfiguratorRuntimeQueryData,
} from "~/features/configurator-v2/common/data/configuratorConfigurationQuery";
import {
  SavedConfigurationDetailsCell,
  SavedConfigurationDetailsLayout,
  SavedConfigurationsTableRow,
} from "~/features/savedConfigurations/savedConfigurationsStyles";

interface Props {
  index: number;
  item: SavedConfigurationItem;
}

const IS_ALT_EXTERNAL_CONTENT = /^[a-zA-Z0-9_-]+$/;
export const isConfiguratorV2SavedConfiguration = (item: Pick<SavedConfigurationItem, "schaefflerId">) =>
  isTimelineConfiguratorProductCode(item.schaefflerId);

export const useConfiguratorV2SavedConfigurationLoad = (code: string) => {
  const queryClient = useQueryClient();
  const loadMutation = useMutation({
    mutationFn: loadConfiguratorV2,
    onSuccess: (response) => {
      const loadedProductCode = response.data.productCode;
      queryClient.setQueryData<ConfiguratorRuntimeQueryData>(configuratorConfigurationKey(loadedProductCode), {
        configuration: {
          ...adaptConfiguratorConfiguration(response.data),
          helpUrl: response.context.helpUrl?.trim() || null,
        },
        context: adaptConfiguratorRuntimeContext(response.context),
        initialExpansion: createConfiguratorInitialExpansion("load"),
      });
      markNextConfiguratorInitialExpansion(loadedProductCode, "load");
      redirectToUrl(`/${loadedProductCode}/configuratorPage/LinearConfigurator`);
    },
  });

  return {
    error: loadMutation.error,
    isPending: loadMutation.isPending,
    load: () => {
      if (!loadMutation.isPending) {
        loadMutation.mutate({ savedConfigurationCode: code });
      }
    },
  };
};

const SavedConfigurationsTableEntry: FC<Props> = ({ index, item }) => {
  const { t } = useTranslation();

  const [name, setName] = useState(item.name);
  const [comment, setComment] = useState(item.comment);
  const contextData = useContext(SavedConfigContext);

  const code = item.code;

  const normalizedName = name.trim();
  const invalidName = !normalizedName || normalizedName.length > 255;
  const isEdited = item.name !== normalizedName;
  const saveItem = () => contextData.updateItem(code, normalizedName, comment);
  const deleteItem = () => contextData.deleteItem(code);
  const selectItem = (newVal: boolean) => contextData.selectItem(code, newVal);

  const updateComment = (newComment: string) => {
    contextData.updateItem(code, name, newComment);
  };

  const updateShowComment = (newValue: boolean) => {
    contextData.toggleComment(code, newValue);
  };

  const shareConfigurationsViaSalesPartnerFinder = (items: SavedConfigurationItem[]) => {
    contextData.shareItemsViaSalesPartnerFinder(items);
  };

  const configuratorType = getConfiguratorTypeFromProductCode(item.schaefflerId) ?? ConfiguratorType.MetricShaft;

  const url = GET_LINEAR_CONFIGURATOR_URL_FOR_ITEM(item.code, item.importUrlPrefix);
  const isConfiguratorV2 = isConfiguratorV2SavedConfiguration(item);
  const configuratorV2Load = useConfiguratorV2SavedConfigurationLoad(item.code);
  const actionProps = isConfiguratorV2
    ? {
        component: "button" as const,
        disabled: configuratorV2Load.isPending,
        onClick: configuratorV2Load.load,
      }
    : {
        href: resolveUrl(url),
      };

  const isCustomer = userIsCustomer();

  const [shareConfigurationsMenuAnchorEl, setShareConfigurationsMenuAnchorEl] = useState<Element | null>(null);
  const [isShareConfigurationsMenuOpen, setIsShareConfigurationsMenuOpen] = useState(false);
  const [isShareConfigurationsViaEmailPopupOpen, setIsShareConfigurationsViaEmailPopupOpen] = useState(false);

  // TODO - Missing configurator type
  const xmlT = useForcedConfiguratorTranslations(item.schaefflerId).t;
  const isOldConfigurator = item.schaefflerId === UNIT_CONFIGURATOR_PRODUCT_CODE;
  const resolvedMediaUrl = item.useXmlTranslation && xmlT ? xmlT(item.mediaUrl) : item.mediaUrl;
  const imageMissing = !resolvedMediaUrl?.trim();

  const product: Pick<
    ProductData,
    "images" | "manufacturerLogoUrl" | "brandLogoUrl" | "url" | "isOptime" | "sapMaterialNumber" | "code"
  > = useMemo(() => {
    return {
      images: [
        {
          imageType: ImageType.primary,
          url: resolvedMediaUrl,
          altText: "",
          format: ImageFormat.thumbnail,
        },
      ],
      manufacturerLogoUrl: item.manufacturerUrl || undefined,
      brandLogoUrl: undefined,
      url,
      isOptime: false,
      sapMaterialNumber: "",
      code: item.schaefflerId,
    };
  }, [item.manufacturerUrl, item.schaefflerId, resolvedMediaUrl, url]);

  const [commentRef, toggleComment] = useShowAndScrollInput(() => updateShowComment(!item.showComment));

  const shortDescription = item.shortDescription ?? "";
  let descriptionProps;
  if (shortDescription.includes("http")) {
    descriptionProps = {
      content: shortDescription,
    };
  } else if (IS_ALT_EXTERNAL_CONTENT.test(shortDescription)) {
    const url = `https://cdn.schaeffler-ecommerce.com/frontend/linear/${shortDescription}_en-GB.html`;
    descriptionProps = {
      content: url,
    };
  } else {
    descriptionProps = {
      forceXmlConfiguratorCode: isOldConfigurator ? undefined : item.schaefflerId,
      content: shortDescription,
    };
  }

  return (
    <SavedConfigurationsTableRow>
      <div>{index}</div>

      <div className={"d-flex mx-3"}>
        <Link
          {...actionProps}
          className={"mb-3 mr-3"}
          color="inherit"
          aria-label={t("page.savedLinearConfigurations.openImage", "Open saved configuration image")}
          sx={{ p: 0, alignSelf: "flex-start", cursor: "pointer" }}
        >
          {imageMissing ? (
            <Box
              data-testid="saved-configuration-image-placeholder"
              sx={(theme) => ({
                flex: "none",
                width: CART_PRODUCT_IMAGE_WIDTHS.product,
                height: CART_PRODUCT_IMAGE_WIDTHS.product,
                mr: CART_PRODUCT_IMAGE_WIDTHS.gap,
                backgroundColor: theme.palette.surfaceContainer?.low ?? theme.palette.grey[200],
              })}
            />
          ) : (
            <ProductImage product={product} widths={CART_PRODUCT_IMAGE_WIDTHS} clickable={false} />
          )}
        </Link>
        <Stack gap={1.25}>
          <Link
            {...actionProps}
            color="inherit"
            aria-label={t("page.savedLinearConfigurations.open", "Open saved configuration")}
            sx={{
              minWidth: 0,
              p: 0,
              alignSelf: "flex-start",
              textAlign: "left",
              textTransform: "none",
              cursor: "pointer",
            }}
          >
            <ProductDesignation
              size={"title-medium"}
              designation={item.designation}
              isPreferred={false}
              tooltipSide={"bottom"}
            />
          </Link>
          {isConfiguratorV2 && configuratorV2Load.error && (
            <Stack direction="row" gap={1} alignItems="center">
              <Box role="alert" color="error.main">
                {t("page.savedLinearConfigurations.loadError", "This configuration could not be opened.")}
              </Box>
              <Button size="small" variant="text" onClick={configuratorV2Load.load}>
                {t("generic.retry", "Retry")}
              </Button>
            </Stack>
          )}
          <p className="mt-1 mb-0">
            <LanguageAwareExternalContent {...descriptionProps} className={"scha__lc__external-label"} />
          </p>
        </Stack>
      </div>

      <SavedConfigurationDetailsCell>
        <Card className={"py-3"} disableHover={true}>
          <SavedConfigurationDetailsLayout>
            <Box sx={{ minWidth: 0 }}>
              <Cell>
                <div className={"d-flex flex-align-baseline w-100"}>
                  <TextField
                    fullWidth
                    label={t("page.savedLinearConfigurations.table.header.name")}
                    onChange={(e) => setName(e.target.value)}
                    value={name}
                    inputProps={{ maxLength: 255 }}
                    error={isEdited && invalidName}
                    helperText={
                      isEdited && invalidName
                        ? t("page.linear.saveConfig.helperLabel", "Enter a configuration name")
                        : undefined
                    }
                  />
                  {isEdited && (
                    <div className={"ml-3"}>
                      <Button
                        size={"medium"}
                        color={"secondary"}
                        onClick={saveItem}
                        disabled={item.isBeingUpdated || invalidName}
                      >
                        {t("generic.save")}
                      </Button>
                    </div>
                  )}
                </div>
              </Cell>
              <Box sx={{ ml: 4.75, mt: 3 }}>
                {item.showComment && (
                  <CommentInput
                    value={item.comment}
                    onChange={updateComment}
                    ref={commentRef}
                    label={t("wishlistPage.comment.label")}
                  />
                )}
              </Box>
            </Box>
            <Cell justifyEnd={true}>
              <Stack gap={2} direction={"row"}>
                {canSeeSalesFinder() && !isCustomer && (
                  <>
                    <ActionIconButton
                      className={"scha_wishlist_deleteitem"}
                      icon={<ShareIcon />}
                      disabled={item.isBeingDeleted}
                      onClick={(e) => {
                        setShareConfigurationsMenuAnchorEl(e.currentTarget);
                        setIsShareConfigurationsMenuOpen(true);
                      }}
                    />
                    <ShareConfigurationsMenu
                      anchorEl={shareConfigurationsMenuAnchorEl}
                      isOpen={isShareConfigurationsMenuOpen}
                      onClose={() => setIsShareConfigurationsMenuOpen(false)}
                      shareConfigurationsViaEmail={() => {
                        setIsShareConfigurationsMenuOpen(false);
                        setIsShareConfigurationsViaEmailPopupOpen(true);
                      }}
                      shareConfigurationsViaSalesPartnerFinder={() => {
                        setIsShareConfigurationsMenuOpen(false);
                        shareConfigurationsViaSalesPartnerFinder([item]);
                      }}
                    />
                    {isShareConfigurationsViaEmailPopupOpen && (
                      <ShareConfigurationsViaEmailPopup
                        onClose={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                        items={[item]}
                        onSuccess={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                      />
                    )}
                  </>
                )}
                {isCustomer && (
                  <>
                    <ActionIconButton
                      className={"scha_wishlist_deleteitem"}
                      icon={<ShareIcon />}
                      disabled={item.isBeingDeleted}
                      onClick={(e) => {
                        setIsShareConfigurationsViaEmailPopupOpen(true);
                      }}
                    />
                    {isShareConfigurationsViaEmailPopupOpen && (
                      <ShareConfigurationsViaEmailPopup
                        onClose={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                        items={[item]}
                        onSuccess={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                      />
                    )}
                  </>
                )}
                <CommentButton
                  onClick={toggleComment}
                  open={item.showComment}
                  fontSize={"small"}
                  className={"scha__saved-configurations__comment-button"}
                />
                <DefaultConfirmationContext onConfirm={deleteItem}>
                  {(openConfirmation) => (
                    <ActionIconButton
                      onClick={openConfirmation}
                      className={"scha_wishlist_deleteitem"}
                      icon={<DeleteIcon />}
                      disabled={item.isBeingDeleted}
                    />
                  )}
                </DefaultConfirmationContext>

                <Checkbox
                  checked={item.isSelected}
                  className={"scha_wishlist_checkbox"}
                  id={"wishlist_small_icon_checkbox"}
                  onCheck={selectItem}
                />
              </Stack>
            </Cell>
          </SavedConfigurationDetailsLayout>
        </Card>
      </SavedConfigurationDetailsCell>
    </SavedConfigurationsTableRow>
  );
};

const Cell: React.FC<{ justifyEnd?: boolean; children?: ReactNode }> = ({ children, justifyEnd }) => {
  const cls = classNames("px-3 d-flex flex-align-start", justifyEnd && "flex-justify-end");
  return <div className={cls}>{children}</div>;
};

export default SavedConfigurationsTableEntry;
